import { makeObservable, observable, action, runInAction } from "mobx";
import * as recDb from "./recordingDb";
import { startCaptureLoop, resolveSourceCanvas } from "./frameCapture";
import { startMicCapture } from "./micCapture";
import { pickClientThinnedIndices } from "./clientFrameSample";
import { compile, CompileHttpError } from "./compileClient";
import { transcribe as transcribeAudio } from "./transcribeClient";
import { createWorkflow } from "../workflowsApi";

const SOFT_BYTES_CAP = 250 * 1024 * 1024;
const HARD_CAP_MS    = 15 * 60 * 1000;
const SOFT_WARN_MS   = 10 * 60 * 1000;
const QUOTA_FAILURE_THRESHOLD = 3;
// Minimum audio blob size to consider "real audio captured." Below this it's
// just the container header — see the comment in stopRecording.
const AUDIO_MIN_USEFUL_BYTES = 1024;

export class RecordingController {
  stage = "idle";             // "idle" | "recording" | "captured" | "compiling" | "reviewing" | "saved"
  draftId = null;
  elapsedMs = 0;
  micLevel = 0;
  phase = null;
  phaseMessage = "";
  playbook = null;
  compileMeta = null;
  error = null;
  drafts = [];
  totalBytes = 0;
  intervalMs = 1500;
  lastSavedWorkflowId = null;
  autoStoppedReason = null;  // "hard_cap" when the 15-min cap auto-stopped this recording
  transcript = null;         // [{t_ms, text}] once transcribed at the captured stage; null = not yet
  transcribing = false;      // true while the standalone /transcribe call is in flight
  capturedMeta = null;       // {durationMs, frameCount, hasAudio} summary shown on the captured screen

  constructor({ userId, guac, intervalMs }) {
    this._userId = userId;
    this._guac = guac;
    this.intervalMs = intervalMs ?? 1500;

    makeObservable(this, {
      stage: observable,
      draftId: observable,
      elapsedMs: observable,
      micLevel: observable,
      phase: observable,
      phaseMessage: observable,
      playbook: observable.ref,
      compileMeta: observable.ref,
      error: observable.ref,
      drafts: observable.shallow,
      totalBytes: observable,
      intervalMs: observable,
      lastSavedWorkflowId: observable,
      autoStoppedReason: observable,
      transcript: observable.ref,
      transcribing: observable,
      capturedMeta: observable.ref,
      _setStage: action,
      _setTranscript: action,
      _setTranscribing: action,
      _setCapturedMeta: action,
      _setMicLevel: action,
      _setElapsed: action,
      _setDrafts: action,
      _setPhase: action,
      _setPlaybook: action,
      _setError: action,
      _setInterval: action,
    });
  }

  // Action helpers (decorated above).
  _setStage(s)             { this.stage = s; }
  _setMicLevel(v)          { this.micLevel = v; }
  _setElapsed(ms)          { this.elapsedMs = ms; }
  _setDrafts(rows, total)  { this.drafts = rows; this.totalBytes = total; }
  _setPhase(phase, msg)    { this.phase = phase; this.phaseMessage = msg || ""; }
  _setPlaybook(pb, meta)   { this.playbook = pb; this.compileMeta = meta; }
  _setError(err)           { this.error = err; }
  _setInterval(ms)         { this.intervalMs = ms; }
  _setTranscript(t)        { this.transcript = t; }
  _setTranscribing(v)      { this.transcribing = v; }
  _setCapturedMeta(m)      { this.capturedMeta = m; }

  // Public API for this task — lifecycle actions added in Task 9.

  async loadDrafts() {
    const [rows, total] = await Promise.all([
      recDb.listMeta(this._userId),
      recDb.totalBytes(this._userId),
    ]);
    this._setDrafts(rows, total);
  }

  async discardDraft(draftId) {
    await recDb.deleteRecording(this._userId, draftId);
    await this.loadDrafts();
  }

  getFirstFrameBlob(draftId) {
    return recDb.getFirstFrame(this._userId, draftId);
  }

  getFramesBytes(draftId) {
    return recDb.framesBytes(this._userId, draftId);
  }

  // All captured frames for the captured-stage filmstrip, mapped from the
  // db's {t, blob} to the UI's {tMs, blob}. Ordered by timestamp. Showing all
  // (not a sample) lets the user find and delete a specific frame.
  async getCapturedFrames() {
    const frames = await recDb.listFrames(this._userId, this._draftId);
    return (frames || []).map((f) => ({ tMs: f.t, blob: f.blob }));
  }
}

Object.assign(RecordingController.prototype, {

  async startRecording({ withNarration = true } = {}) {
    if (this.stage === "recording" || this._starting) return;
    this._starting = true;

    try {
      const draftId = (crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2));
      const startedAt = Date.now();

      let displayWidth = null;
      let displayHeight = null;
      try {
        const canvas = resolveSourceCanvas(this._guac);
        displayWidth  = canvas.width;
        displayHeight = canvas.height;
      } catch (_) {
        // Canvas not ready yet — leave nulls; Kyoto falls back to placeholders.
      }

      await recDb.putMeta(this._userId, {
        id: draftId,
        createdAt: startedAt,
        status: "recording",
        screenshotIntervalMs: this.intervalMs,
        displayWidth,
        displayHeight,
      });

    let mic = null;
    if (withNarration) {
      try {
        mic = await startMicCapture({
          onLevel: (v) => this._setMicLevel(v),
        });
      } catch (e) {
        // Permission denied → ask the user whether to proceed without narration.
        const proceed = window.confirm(
          "Record without narration? The AI will compile from screenshots only, " +
          "which works but is less accurate.",
        );
        if (!proceed) {
          // Abort: clean up the seeded draft and stay idle.
          try { await recDb.deleteRecording(this._userId, draftId); } catch (_) {}
          return;
        }
        // Frames-only mode — sentinel value tells the UI to hide the mic meter.
        this._setMicLevel(null);
        mic = null;
      }
    } else {
      // User opted out of narration before we ever prompted for the mic.
      // Take the frames-only path without bothering them with a permission
      // dialog. Same sentinel as the permission-denied branch above so the
      // recording pill knows to hide the mic meter.
      this._setMicLevel(null);
    }

    const pendingFrameWrites = new Set();
    let consecutiveWriteFailures = 0;
    let frameCount = 0;

    const captureLoop = startCaptureLoop({
      guac: this._guac,
      intervalMs: this.intervalMs,
      onFrame: (tMs, blob) => {
        if (this._stopping) return;  // post-stopRecording tick — drop it
        frameCount++;
        const p = recDb.putFrame(this._userId, draftId, tMs, blob).then(
          () => {
            consecutiveWriteFailures = 0;
            pendingFrameWrites.delete(p);
          },
          (err) => {
            consecutiveWriteFailures++;
            pendingFrameWrites.delete(p);
            if (consecutiveWriteFailures >= QUOTA_FAILURE_THRESHOLD) {
              this._abortFromQuota(err);
            }
          }
        );
        pendingFrameWrites.add(p);
      },
    });

    const tickId = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      this._setElapsed(elapsed);
      if (elapsed >= HARD_CAP_MS && !this._stopping) {
        runInAction(() => { this.autoStoppedReason = "hard_cap"; });
        this.stopRecording();
      }
    }, 250);

    this._draftId     = draftId;
    this._startedAt   = startedAt;
    this._stopFrames  = () => captureLoop.stop();
    this._mic         = mic;
    this._tickId      = tickId;
    this._getFrameCount = () => frameCount;
    this._pendingFrameWrites = pendingFrameWrites;
    this._stopping    = false;

    runInAction(() => {
      this.draftId = draftId;
      this.error   = null;
      this.autoStoppedReason = null;  // clear last recording's reason
    });
    this._setTranscript(null);  // fresh take — drop the prior take's narration
    this._setCapturedMeta(null);
    this._setStage("recording");
    } finally {
      this._starting = false;
    }
  },

  async stopRecording() {
    if (this.stage !== "recording" || this._stopping) return;
    this._stopping = true;

    this._stopFrames();
    clearInterval(this._tickId);

    await Promise.all([...this._pendingFrameWrites]);

    let audioMime = null;
    let audioBytes = 0;
    if (this._mic) {
      const { blob, mime } = await this._mic.stop();
      // Under ~1 KB is just the container header (EBML for webm, ftyp for mp4)
      // with no encoded audio clusters. Happens when the mic is muted or
      // disconnected: MediaRecorder writes the header but the Opus encoder
      // never produces frames. Treat as no-audio so kyoto takes the
      // frames-only compile path instead of failing at Whisper with 400.
      if (blob && blob.size >= AUDIO_MIN_USEFUL_BYTES) {
        await recDb.putAudio(this._userId, this._draftId, blob);
        audioMime  = mime;
        audioBytes = blob.size;
      } else if (blob && blob.size > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Copilot] mic produced only ${blob.size} bytes (header-only — ` +
          `no audio signal). Compiling from screenshots only.`,
        );
      }
    }

    const durationMs   = Date.now() - this._startedAt;
    const frameCount   = this._getFrameCount();

    const priorMeta = (await recDb.getMeta(this._userId, this._draftId)) || {};
    await recDb.putMeta(this._userId, {
      ...priorMeta,
      id: this._draftId,
      createdAt: this._startedAt,
      status: "captured",
      durationMs,
      frameCount,
      audioMime:  audioMime  || null,
      audioBytes: audioBytes || 0,
    });

    // Bank the take and let the user review it — no auto-compile. They can
    // optionally transcribe the narration, then compile or re-record from the
    // captured screen. This mirrors the prototype, where compile is always a
    // deliberate action.
    this._setCapturedMeta({
      durationMs,
      frameCount,
      hasAudio: !!audioMime,
    });
    this._setStage("captured");
    this._setError(null);
  },

  async compile() {
    if (this.stage !== "captured") return;
    this._setStage("compiling");
    this._setError(null);
    await this._compileCurrentDraft();
  },

  async transcribe() {
    if (this.stage !== "captured" || this.transcribing) return;
    const meta = await recDb.getMeta(this._userId, this._draftId);
    if (!meta || !meta.audioMime) return;  // frames-only take — nothing to transcribe
    this._setTranscribing(true);
    this._setError(null);
    try {
      const audioBlob = await recDb.getAudioBlob(this._userId, this._draftId);
      const segments = await transcribeAudio({ audioBlob, audioMime: meta.audioMime });
      this._setTranscript(segments);
      await this._persistMetaPatch(this._draftId, { whisperTranscript: segments });
    } catch (e) {
      this._setError({
        code:    (e.body && e.body.error) || "transcribe_failed",
        message: (e.body && e.body.message) || e.message || "Transcription failed",
        retryable: true,
      });
    } finally {
      this._setTranscribing(false);
    }
  },

  updateTranscript(transcript) {
    // User-edited narration at the captured stage. Cached in memory (flows to
    // compile, which then skips Whisper) and persisted so a resumed draft keeps
    // the corrections.
    this._setTranscript(transcript);
    this._persistMetaPatch(this._draftId, { whisperTranscript: transcript });
  },

  async deleteCapturedFrames(tMsList) {
    const list = tMsList || [];
    for (const tMs of list) {
      await recDb.deleteFrame(this._userId, this._draftId, tMs);
    }
    if (this.capturedMeta && list.length) {
      this._setCapturedMeta({
        ...this.capturedMeta,
        frameCount: Math.max(0, (this.capturedMeta.frameCount || 0) - list.length),
      });
    }
  },

  async reRecord() {
    const draftId = this._draftId;
    this._setTranscript(null);
    if (draftId) {
      try { await recDb.deleteRecording(this._userId, draftId); } catch (_) {}
    }
    runInAction(() => {
      this.draftId   = null;
      this.elapsedMs = 0;
      this.micLevel  = 0;
      this.playbook  = null;
    });
    this._setStage("idle");
    await this.startRecording();
    await this.loadDrafts();
  },

  async _compileCurrentDraft() {
    const draftId = this._draftId;

    if (this._compileAbort) {
      try { this._compileAbort.abort(); } catch (_) {}
    }
    const abortCtl = new AbortController();
    this._compileAbort = abortCtl;
    const myRunId = (this._compileRunId = (this._compileRunId || 0) + 1);
    const isCurrent = () => myRunId === this._compileRunId;

    const meta   = await recDb.getMeta(this._userId, draftId);
    const frames = await recDb.listFrames(this._userId, draftId);
    const audioBlob = meta && meta.audioMime
      ? await recDb.getAudioBlob(this._userId, draftId)
      : null;

    const indices = pickClientThinnedIndices(frames.length);
    const thinnedFrames = indices.map((i) => frames[i]);

    // Compile-contract meta shape (see kyoto compile plan). Persisted draft meta
    // uses `id`/`createdAt`/`frameCount`; rename for the wire so Kyoto can read
    // capturedFrameCount, uploadedFrameCount, capturedAt directly.
    const compileMetaShape = {
      recordingId:          meta?.id || null,
      capturedAt:           meta?.createdAt != null ? new Date(meta.createdAt).toISOString() : null,
      durationMs:           meta?.durationMs ?? null,
      capturedFrameCount:   meta?.frameCount ?? frames.length,
      uploadedFrameCount:   thinnedFrames.length,
      screenshotIntervalMs: meta?.screenshotIntervalMs ?? this.intervalMs,
      displayWidth:         meta?.displayWidth  ?? null,
      displayHeight:        meta?.displayHeight ?? null,
      audioMime:            meta?.audioMime ?? null,
    };

    // Prefer the transcript the user already produced at the captured stage
    // (cached on the controller, or persisted to meta on resume). When absent,
    // Kyoto transcribes the audio itself. Either path is valid server-side.
    const transcript = this.transcript || (meta && meta.whisperTranscript) || null;

    const bundle = {
      meta: compileMetaShape,
      frames: thinnedFrames,
      audioBlob: audioBlob || null,
      transcript,
    };

    let resultPayload = null;
    let errorPayload  = null;

    try {
      await compile({
        bundle,
        signal: abortCtl.signal,
        onPhase: ({ phase, message }) => {
          if (!isCurrent()) return;
          this._setPhase(phase, message);
        },
        onResult: (data) => {
          if (!isCurrent()) return;
          resultPayload = data;
          this._setPlaybook(data.playbook, data.compileMeta);
          this._setStage("reviewing");
          this._setPhase(null, "");
        },
        onError: (data) => {
          if (!isCurrent()) return;
          errorPayload = data;
          this._setError({
            code: data.code,
            message: data.message,
            retryable: data.retryable !== false,
          });
          this._setStage("compiling");
          this._setPhase(null, "");
        },
      });
    } catch (e) {
      if (!isCurrent()) return;  // aborted — caller already moved on
      // Pre-stream JSON error from compileClient (CompileHttpError).
      errorPayload = {
        code: (e.body && e.body.error) || `http_${e.status}`,
        message: (e.body && e.body.message) || e.message,
        retryable: e.status >= 500 || e.status === 503,
        retryAfter: e.retryAfter,
      };
      this._setError(errorPayload);
    }

    if (!isCurrent()) return;

    if (resultPayload !== null) {
      await this._persistMetaPatch(draftId, {
        playbook:     resultPayload.playbook,
        compileMeta:  resultPayload.compileMeta,
        status:       "compiled",
      });
    } else if (errorPayload !== null) {
      await this._persistMetaPatch(draftId, {
        status:       "compile_failed",
        compileError: errorPayload,
      });
    }
  },

  async _abortFromQuota(originalError) {
    if (this._stopping) return;
    this._stopping = true;

    if (this._stopFrames) this._stopFrames();
    if (this._tickId) clearInterval(this._tickId);

    await Promise.allSettled([...this._pendingFrameWrites]);

    if (this._mic) {
      try { await this._mic.stop(); } catch (_) {}
    }

    try {
      await recDb.deleteRecording(this._userId, this._draftId);
    } catch (_) {}

    const message = (originalError && originalError.message) || "Storage quota exceeded";

    runInAction(() => {
      this.draftId  = null;
      this.elapsedMs = 0;
      this.micLevel  = 0;
    });

    this._setError({
      code:      "quota_exhausted",
      message,
      cause:     originalError,
      retryable: false,
    });
    this._setStage("idle");

    await this.loadDrafts();
  },

  async _persistMetaPatch(draftId, patch) {
    try {
      const meta = await recDb.getMeta(this._userId, draftId);
      await recDb.putMeta(this._userId, { ...meta, ...patch });
    } catch (err) {
      console.warn("[RecordingController] _persistMetaPatch failed:", err);
    }
  },

  async recompile() {
    if (this.stage !== "reviewing" && this.stage !== "compiling") return;
    this._setPlaybook(null, null);
    this._setError(null);
    this._setStage("compiling");
    await this._compileCurrentDraft();
  },

  async save({ name, summary, playbookOverride } = {}) {
    if (this.stage !== "reviewing") return;

    const playbook = playbookOverride ?? this.playbook;

    try {
      const saved = await createWorkflow({ name, summary, playbook });
      await recDb.deleteRecording(this._userId, this._draftId);
      runInAction(() => {
        this.draftId = null;
        this.error   = null;
        this.lastSavedWorkflowId = (saved && saved.id) || null;
      });
      this._setStage("saved");
      await this.loadDrafts();
    } catch (e) {
      this._setError({
        code:    e.code    || "unknown",
        message: e.message_text || e.message || "Save failed",
        errors:  e.errors  || null,
      });
      // Stay on reviewing
    }
  },

  closeWizard() {
    if (this._compileAbort) {
      try { this._compileAbort.abort(); } catch (_) {}
      this._compileAbort = null;
    }
    this._compileRunId = (this._compileRunId || 0) + 1;
    this._setStage("idle");
    this._setPlaybook(null, null);
    this._setError(null);
    this._setPhase(null, "");
    this._setTranscript(null);
    this._setCapturedMeta(null);
    runInAction(() => {
      this.draftId = null;
      this.elapsedMs = 0;
      this.micLevel  = 0;
    });
    // The capture → compile path persists the draft to IndexedDB but doesn't
    // touch the `drafts` cache. Refresh it on close so a just-recorded draft
    // shows in the library's Drafts list without waiting for a page reload to
    // re-attach the controller.
    this.loadDrafts().catch(() => {});
  },

  async resumeDraft(draftId) {
    const meta = await recDb.getMeta(this._userId, draftId);
    this._draftId = draftId;
    runInAction(() => { this.draftId = draftId; });

    this._setTranscript((meta && meta.whisperTranscript) || null);

    if (meta && meta.playbook) {
      this._setPlaybook(meta.playbook, meta.compileMeta || null);
      this._setStage("reviewing");
    } else {
      // Uncompiled take — drop the user back on the captured screen to review
      // (and optionally transcribe) before spending a compile.
      this._setStage("captured");
    }
  },

  setIntervalMs(ms) {
    try {
      this._setInterval(ms);
      localStorage.setItem(`cua.recording.intervalMs.${this._userId}`, String(ms));
    } catch (_) {}
  },

  loadIntervalFromStorage() {
    try {
      const raw = localStorage.getItem(`cua.recording.intervalMs.${this._userId}`);
      if (raw !== null) {
        const ms = parseInt(raw, 10);
        if (!isNaN(ms) && ms >= 1000 && ms <= 5000) {
          this._setInterval(ms);
        }
      }
    } catch (_) {}
  },
});
